export interface RawDistribution {
  deltaDistribution: Array<{ deltaMin: number; count: number }>;
  offsetDistribution: Array<{ offsetMin: number; count: number }>;
}

/**
 * Parse a UTC timestamp cell to epoch ms.
 *
 * The CSV's first column is UTC wall-clock ("YYYY-MM-DD HH:MM:SS", no zone).
 * `new Date("YYYY-MM-DD HH:MM:SS")` interprets that as **local** time, which
 * injects a one-hour jump at the local DST boundary: across spring-forward the
 * skipped local hour collides with the next, producing spurious Δt values
 * (e.g. a 0-minute step) in the step distribution. Parse as UTC instead.
 *
 * Strings that carry an explicit zone (Z or ±HH:MM) are parsed natively.
 */
export function parseUtcMs(cell: string): number | null {
  const s = cell.trim();
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) {
    const t = Date.parse(s);
    return Number.isNaN(t) ? null : t;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(s);
  if (!m) return null;
  const [, y, mo, d, h, mi, se] = m;
  const t = Date.UTC(+y, +mo - 1, +d, +h, +mi, se ? +se : 0);
  return Number.isNaN(t) ? null : t;
}

/**
 * Compute the distributions used by RawDataDistribution.
 * @param timestamps Ascending-sorted epoch ms array parsed from the first CSV column.
 */
export function computeRawDistribution(timestamps: number[]): RawDistribution {
  if (timestamps.length < 2) return { deltaDistribution: [], offsetDistribution: [] };

  const deltaCounts = new Map<number, number>();
  for (let i = 1; i < timestamps.length; i++) {
    const deltaMin = (timestamps[i] - timestamps[i - 1]) / 60_000;
    const key = Math.round(deltaMin * 1e6) / 1e6; // avoid FP noise
    deltaCounts.set(key, (deltaCounts.get(key) ?? 0) + 1);
  }
  const deltaDistribution = [...deltaCounts.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([deltaMin, count]) => ({ deltaMin, count }));

  const offsetCounts = new Map<number, number>();
  for (let i = 1; i < timestamps.length; i++) {
    const ts = new Date(timestamps[i]);
    const minutesSinceHour = ts.getUTCMinutes() + ts.getUTCSeconds() / 60 + ts.getUTCMilliseconds() / 60_000;
    const deltaMin = (timestamps[i] - timestamps[i - 1]) / 60_000;
    if (deltaMin <= 0) continue;
    const ofst = minutesSinceHour % deltaMin;
    const key = Math.round(ofst * 1e6) / 1e6;
    offsetCounts.set(key, (offsetCounts.get(key) ?? 0) + 1);
  }
  const offsetDistribution = [...offsetCounts.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([offsetMin, count]) => ({ offsetMin, count }));

  return { deltaDistribution, offsetDistribution };
}
